import React, { useEffect, useState } from 'react';
import { useUser } from '../provider/UserProvider';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import CallIcon from '@mui/icons-material/Call';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PersonIcon from '@mui/icons-material/Person';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import moment from 'moment';
import axiosInstance from '../utlis/axiosInstance';
import { useNavigate } from 'react-router-dom';
import styles from './SupportTickets.module.css';
import { useDevicesApi } from '../utlis/useDevicesApi';
import { Autocomplete, TextField } from '@mui/material';

interface SupportTicket {
  id: number;
  ticket_id: string;
  title: string;
  description: string;
  contact_number: string;
  email_address: string;
  priority: string;
  status: string;
  resolution_notes: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
  created_by: {
    id: number;
    name: string;
    email: string;
  };
  assigned_to: any | null;
  attributes?: {
    device_id?: string;
    vehicle_name?: string;
  };
}

interface TicketFormData {
  title: string;
  description: string;
  contact_number: string;
  email_address: string;
  priority: string;
  attributes: {
    device_id: string;
    vehicle_name?: string;
  };
}

const priorityOptions = [
  { label: 'Low', value: 'low' },
  { label: 'Medium', value: 'medium' },
  { label: 'High', value: 'high' },
  { label: 'Critical', value: 'critical' },
];

const statusOptions = [
  { label: 'All Tickets', value: 'all' },
  { label: 'Open Tickets', value: 'open' },
  { label: 'Closed Tickets', value: 'closed' },
];

const getPriorityClass = (priority: string) => {
  switch (priority.toLowerCase()) {
    case 'low':
      return styles.priorityLow;
    case 'medium':
      return styles.priorityMedium;
    case 'high':
      return styles.priorityHigh;
    case 'critical':
      return styles.priorityCritical;
    default:
      return styles.priorityMedium;
  }
};

const getStatusClass = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'open':
      return styles.statusOpen;
    case 'in_progress':
      return styles.statusInProgress;
    case 'resolved':
      return styles.statusResolved;
    case 'closed':
      return styles.statusClosed;
    default:
      return styles.statusOpen;
  }
};

const SupportTickets: React.FC = () => {
  const navigate = useNavigate();
  const { permissions: userPermissions, user } = useUser();
  const { devices } = useDevicesApi();

  // Check if user is admin
  const isAdmin = user?.user?.administrator;

  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [filteredTickets, setFilteredTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [formData, setFormData] = useState<TicketFormData>({
    title: '',
    description: '',
    contact_number: '',
    email_address: '',
    priority: 'medium',
    attributes: {
      device_id: '',
      vehicle_name: '',
    },
  });
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('open');
  const [vehicleFilter, setVehicleFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Confirmation states
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [ticketToClose, setTicketToClose] = useState<SupportTicket | null>(null);

  useEffect(() => {
    fetchTickets();
  }, []);

  // Filter tickets based on search term and status filter
  useEffect(() => {
    let filtered = tickets;

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter((ticket) => (statusFilter === 'open' ? ticket.status?.toLowerCase() !== 'closed' : ticket.status?.toLowerCase() === 'closed'));
    }

    // Filter by vehicle
    if (vehicleFilter) {
      filtered = filtered.filter((ticket) => {
        const vehicleName = ticket.attributes?.vehicle_name || '';
        return String(vehicleName).toLowerCase() === vehicleFilter.toLowerCase();
      });
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (ticket) => {
          const vehicleName = ticket.attributes?.vehicle_name || '';
          return (
            ticket.title.toLowerCase().includes(searchLower) ||
            ticket.ticket_id.toLowerCase().includes(searchLower) ||
            ticket.description.toLowerCase().includes(searchLower) ||
            ticket.created_by?.name.toLowerCase().includes(searchLower) ||
            ticket.contact_number?.toLowerCase().includes(searchLower) ||
            ticket.email_address?.toLowerCase().includes(searchLower) ||
            String(vehicleName).toLowerCase().includes(searchLower)
          );
        }
      );
    }

    setFilteredTickets(filtered);
  }, [tickets, searchTerm, statusFilter, vehicleFilter]);

  const fetchTickets = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get('/gpstracker/supporttickethandler/');
      if (response?.data?.responseData) {
        const ticketsData = response.data.responseData || [];
        setTickets([...ticketsData].reverse());
      } else {
        setTickets([]);
      }
    } catch (err: any) {
      setError('Failed to load support tickets.');
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const createTicket = async () => {
    if (!formData.title.trim() || !formData.description.trim()) {
      setError('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = await axiosInstance.post('/gpstracker/supporttickethandler/', formData);
      if (response?.status === 200 || response?.status === 201) {
        setSuccessMsg('Support ticket created successfully!');
        setModalOpen(false);
        resetForm();
        fetchTickets();
      } else {
        setError('Failed to create ticket. Please try again.');
      }
    } catch (err: any) {
      setError('Failed to create ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const updateTicketStatus = async (ticketId: number, status: string, resolutionNotes?: string) => {
    try {
      const payload: any = {
        status: status,
      };

      if (resolutionNotes) {
        payload.resolution_notes = resolutionNotes;
      }

      const response = await axiosInstance.post(`/gpstracker/supporttickethandler/${ticketId}/status`, payload);

      if (response?.status === 200) {
        const statusMessage = status === 'closed' ? 'closed' : `updated to ${status.replace('_', ' ')}`;
        setSuccessMsg(`Ticket ${statusMessage} successfully!`);
        fetchTickets();
        if (selectedTicket?.id === ticketId) {
          setSelectedTicket(null);
        }
      } else {
        setError('Failed to update ticket status. Please try again.');
      }
    } catch (err: any) {
      setError('Failed to update ticket status. Please try again.');
    }
  };

  const closeTicket = async (ticketId: number) => {
    await updateTicketStatus(ticketId, 'closed', 'Ticket closed by support team.');
  };

  const handleCloseTicketClick = (ticket: SupportTicket) => {
    setTicketToClose(ticket);
    setShowCloseConfirmation(true);
  };

  const confirmCloseTicket = async () => {
    if (ticketToClose) {
      await closeTicket(ticketToClose.id);
      setShowCloseConfirmation(false);
      setTicketToClose(null);
    }
  };

  const cancelCloseTicket = () => {
    setShowCloseConfirmation(false);
    setTicketToClose(null);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      contact_number: '',
      email_address: '',
      priority: 'medium',
      attributes: {
        device_id: '',
        vehicle_name: '',
      },
    });
  };

  const handleViewTicket = (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('open');
    setVehicleFilter('');
  };

  // Get unique vehicle names from API
  const getUniqueVehicles = () => {
    if (!devices || devices.length === 0) {
      return [];
    }
    const vehicles = devices
      .map((device: any) => device?.name)
      .filter((name): name is string => Boolean(name) && String(name).trim() !== '');
    return Array.from(new Set(vehicles)).sort();
  };

  return (
    <div className={styles.container}>
      <div className={styles.mainContent}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <button
              onClick={() => navigate(-1)}
              className={styles.backButton}
            >
              <ArrowBackIcon sx={{ fontSize: 20 }} />
            </button>
            <HelpOutlineIcon sx={{ fontSize: 28, color: 'white' }} />
            <h1 className={styles.headerTitle}>Support Tickets</h1>
          </div>
          <button
            onClick={() => setModalOpen(true)}
            className={styles.createButton}
          >
            <AddIcon sx={{ fontSize: 18 }} />
            Create Ticket
          </button>
        </div>

        {/* Success/Error Messages */}
        {successMsg && (
          <div className={`${styles.alert} ${styles.success}`}>
            <CheckCircleIcon sx={{ fontSize: 18 }} />
            {successMsg}
          </div>
        )}
        {error && (
          <div className={`${styles.alert} ${styles.error}`}>
            <WarningIcon sx={{ fontSize: 18 }} />
            {error}
          </div>
        )}

        {/* Search and Filter Section */}
        <div className={styles.filterSection}>
          <div className={styles.searchContainer}>
            <div className={styles.searchInputWrapper}>
              <SearchIcon className={styles.searchIcon} />
              <input
                type='text'
                placeholder='Search tickets...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className={styles.clearSearchButton}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </button>
              )}
            </div>
          </div>

          <div className={styles.filterControls}>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={styles.filterToggleButton}
            >
              <FilterListIcon sx={{ fontSize: 18 }} />
              Filters
            </button>

            {(searchTerm || statusFilter !== 'open' || vehicleFilter) && (
              <button
                onClick={clearFilters}
                className={styles.clearFiltersButton}
              >
                Clear All
              </button>
            )}
          </div>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className={styles.filterOptions}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Status Filter:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={styles.filterSelect}
              >
                {statusOptions.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Vehicle Filter:</label>
              <select
                value={vehicleFilter}
                onChange={(e) => setVehicleFilter(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="">All Vehicles</option>
                {getUniqueVehicles().map((vehicle) => (
                  <option
                    key={vehicle}
                    value={vehicle}
                  >
                    {vehicle}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Results Summary */}
        <div className={styles.resultsSummary}>
          <span className={styles.resultsText}>
            Showing {filteredTickets.length} of {tickets.length} tickets
          </span>
        </div>

        {/* Loading State */}
        {loading && (
          <div className={styles.loadingSpinner}>
            <div className={styles.loadingText}>Loading support tickets...</div>
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredTickets.length === 0 && (
          <div className={styles.emptyState}>
            <HelpOutlineIcon className={styles.emptyIcon} />
            <h2 className={styles.emptyTitle}>
              {searchTerm || statusFilter !== 'all' || vehicleFilter ? 'No tickets found matching your criteria' : 'No support tickets found'}
            </h2>
            <p className={styles.emptySubtitle}>
              {searchTerm || statusFilter !== 'all' || vehicleFilter ? 'Try adjusting your search or filters' : 'Create a new ticket to get help with your issues'}
            </p>
          </div>
        )}

        {/* Tickets List - Professional Table Format */}
        {!loading && filteredTickets.length > 0 && (
          <div className={styles.ticketsTableContainer}>
            <div className={styles.tableHeader}>
              <div className={styles.tableRow}>
                <div className={`${styles.tableCell} ${styles.cellTicketId}`}>Ticket ID</div>
                <div className={`${styles.tableCell} ${styles.cellTitle}`}>Title</div>
                <div className={`${styles.tableCell} ${styles.cellPriority}`}>Priority</div>
                <div className={`${styles.tableCell} ${styles.cellStatus}`}>Status</div>
                <div className={`${styles.tableCell} ${styles.cellCreatedBy}`}>Created By</div>
                <div className={`${styles.tableCell} ${styles.cellDate}`}>Created Date</div>
                <div className={`${styles.tableCell} ${styles.cellActions}`}>Actions</div>
              </div>
            </div>

            <div className={styles.tableBody}>
              {filteredTickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className={styles.tableRow}
                >
                  <div className={`${styles.tableCell} ${styles.cellTicketId}`}>
                    <span className={styles.ticketIdText}>#{ticket.ticket_id}</span>
                  </div>
                  <div className={`${styles.tableCell} ${styles.cellTitle}`}>
                    <div className={styles.titleContainer}>
                      <span className={styles.titleText}>{ticket.title}</span>
                      {ticket.contact_number && (
                        <div className={styles.contactInfo}>
                          <CallIcon className={styles.contactIcon} />
                          <span>{ticket.contact_number}</span>
                        </div>
                      )}
                      {ticket.attributes?.vehicle_name && (
                        <div className={styles.contactInfo}>
                          <span style={{ fontWeight: 500 }}>Vehicle: {String(ticket.attributes.vehicle_name)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={`${styles.tableCell} ${styles.cellPriority}`}>
                    <span className={`${styles.priorityChip} ${getPriorityClass(ticket.priority)}`}>{ticket.priority.toUpperCase()}</span>
                  </div>
                  <div className={`${styles.tableCell} ${styles.cellStatus}`}>
                    <span className={`${styles.statusChip} ${getStatusClass(ticket.status)}`}>{ticket.status?.replace('_', ' ').toUpperCase() || 'OPEN'}</span>
                  </div>
                  <div className={`${styles.tableCell} ${styles.cellCreatedBy}`}>
                    <div className={styles.userInfo}>
                      <PersonIcon className={styles.userIcon} />
                      <span>{ticket.created_by?.name}</span>
                    </div>
                  </div>
                  <div className={`${styles.tableCell} ${styles.cellDate}`}>
                    <span className={styles.dateText}>{moment(ticket.created_at).format('DD/MM/YYYY')}</span>
                    <span className={styles.timeText}>{moment(ticket.created_at).format('HH:mm')}</span>
                  </div>
                  <div className={`${styles.tableCell} ${styles.cellActions}`}>
                    <div className={styles.actionButtons}>
                      <button
                        onClick={() => handleViewTicket(ticket)}
                        className={styles.viewButton}
                        title='View Details'
                      >
                        <VisibilityIcon sx={{ fontSize: 18 }} />
                      </button>
                      {ticket.status?.toLowerCase() !== 'closed' && isAdmin && (
                        <button
                          onClick={() => handleCloseTicketClick(ticket)}
                          className={styles.closeButton}
                          title='Close Ticket'
                        >
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Create Ticket Modal */}
        {modalOpen && (
          <div
            className={styles.modalOverlay}
            onClick={() => setModalOpen(false)}
          >
            <div
              className={styles.modal}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle}>Create Support Ticket</h2>
              </div>

              <div className={styles.modalContent}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Title *</label>
                  <input
                    type='text'
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className={styles.formInput}
                    placeholder='Enter ticket title'
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Description *</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className={styles.formTextarea}
                    placeholder='Describe your issue in detail'
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Contact Number</label>
                  <input
                    type='tel'
                    value={formData.contact_number}
                    onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })}
                    className={styles.formInput}
                    placeholder='Enter contact number'
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Email Address</label>
                  <input
                    type='email'
                    value={formData.email_address}
                    onChange={(e) => setFormData({ ...formData, email_address: e.target.value })}
                    className={styles.formInput}
                    placeholder='Enter email address'
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Device</label>
                  <Autocomplete
                    options={devices as any[]}
                    getOptionLabel={(d: any) => d?.name || ''}
                    value={(devices as any[]).find((d: any) => String(d.unique_id) === String(formData.attributes.device_id)) || null}
                    isOptionEqualToValue={(option: any, value: any) => String(option?.unique_id) === String(value?.unique_id)}
                    onChange={(_, newValue: any | null) =>
                      setFormData((prev) => ({
                        ...prev,
                        attributes: { 
                          ...prev.attributes, 
                          device_id: newValue ? String(newValue.unique_id) : '',
                          vehicle_name: newValue ? String(newValue.name) : '',
                        },
                      }))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder='Select device'
                        size='small'
                      />
                    )}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Priority</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className={styles.formSelect}
                  >
                    {priorityOptions.map((option) => (
                      <option
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={styles.modalActions}>
                <button
                  onClick={() => {
                    setModalOpen(false);
                    resetForm();
                  }}
                  className={styles.cancelButton}
                >
                  Cancel
                </button>
                <button
                  onClick={createTicket}
                  className={styles.submitButton}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <div className={styles.loadingSpinner} />
                      Creating...
                    </>
                  ) : (
                    'Create Ticket'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Ticket Details Modal */}
        {selectedTicket && (
          <div
            className={styles.modalOverlay}
            onClick={() => setSelectedTicket(null)}
          >
            <div
              className={styles.modal}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle}>Ticket Details</h2>
              </div>

              <div className={styles.modalContent}>
                <div className={styles.detailSection}>
                  <h3 className={styles.detailTitle}>#{selectedTicket.ticket_id}</h3>
                  <h4 className={styles.detailSubtitle}>{selectedTicket.title}</h4>
                </div>

                <div className={styles.detailSection}>
                  <h5 className={styles.sectionTitle}>Description</h5>
                  <p className={styles.detailDescription}>{selectedTicket.description}</p>
                </div>

                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Priority:</span>
                    <span className={`${styles.priorityChip} ${getPriorityClass(selectedTicket.priority)}`}>{selectedTicket.priority.toUpperCase()}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Status:</span>
                    <span className={`${styles.statusChip} ${getStatusClass(selectedTicket.status)}`}>
                      {selectedTicket.status?.replace('_', ' ').toUpperCase() || 'OPEN'}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Created By:</span>
                    <span className={styles.detailValue}>{selectedTicket.created_by?.name}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Created Date:</span>
                    <span className={styles.detailValue}>{moment(selectedTicket.created_at).format('DD/MM/YYYY HH:mm:ss')}</span>
                  </div>
                  {selectedTicket.contact_number && (
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Contact:</span>
                      <span className={styles.detailValue}>{selectedTicket.contact_number}</span>
                    </div>
                  )}
                  {selectedTicket.email_address && (
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Email:</span>
                      <span className={styles.detailValue}>{selectedTicket.email_address}</span>
                    </div>
                  )}
                  {selectedTicket.attributes?.vehicle_name && (
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Vehicle No.:</span>
                      <span className={styles.detailValue}>{String(selectedTicket.attributes.vehicle_name)}</span>
                    </div>
                  )}
                </div>

                {selectedTicket.resolution_notes && (
                  <div className={styles.detailSection}>
                    <h5 className={styles.sectionTitle}>Resolution Notes</h5>
                    <p className={styles.detailDescription}>{selectedTicket.resolution_notes}</p>
                  </div>
                )}
              </div>

              <div className={styles.modalActions}>
                {selectedTicket.status?.toLowerCase() !== 'closed' && isAdmin && (
                  <button
                    onClick={() => handleCloseTicketClick(selectedTicket)}
                    className={styles.closeTicketButton}
                  >
                    Close Ticket
                  </button>
                )}
                <button
                  onClick={() => setSelectedTicket(null)}
                  className={styles.cancelButton}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Close Ticket Confirmation Modal */}
        {showCloseConfirmation && ticketToClose && (
          <div
            className={styles.modalOverlay}
            onClick={cancelCloseTicket}
          >
            <div
              className={styles.modal}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle}>Confirm Close Ticket</h2>
              </div>

              <div className={styles.modalContent}>
                <div className={styles.confirmationContent}>
                  <WarningIcon className={styles.warningIcon} />
                  <h3 className={styles.confirmationTitle}>Are you sure you want to close this ticket?</h3>
                  <p className={styles.confirmationMessage}>
                    Ticket <strong>#{ticketToClose.ticket_id}</strong> - "{ticketToClose.title}"
                  </p>
                  <p className={styles.confirmationWarning}>This action cannot be undone. The ticket will be marked as closed.</p>
                </div>
              </div>

              <div className={styles.modalActions}>
                <button
                  onClick={cancelCloseTicket}
                  className={styles.cancelButton}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmCloseTicket}
                  className={styles.confirmCloseButton}
                >
                  Close Ticket
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportTickets;
